import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ActionModal } from "../modals/ActionModal";
import { MDBBtn, MDBIcon, MDBInput, MDBSpinner } from "mdb-react-ui-kit";
import { InputProps } from "mdb-react-ui-kit/dist/types/free/forms/Input/types";

export interface InputFieldEditProps {
    children?: (open: () => void, close: () => void, isOpen: boolean) => React.ReactNode;
    action: (value: number|string|null) => Promise<void>;
    title: string;
    value: number|string;
    type?: string;
    confirmText?: string;
    canEdit?: boolean;
    inputTransformation?: (value: number|string|null) => number|string|null;
    inputLabel?: string;
    customInput?: (crtValue: number|string|null, setCrtValue: (value: number|string|null) => void) => React.ReactNode;
    inputProps?: InputProps;
}
export const InputFieldEdit = ({
    children,
    action,
    title,
    confirmText,
    canEdit,
    value,
    type,
    inputTransformation,
    inputLabel,
    customInput,
    inputProps
}: InputFieldEditProps) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [crtValue, setCrtValue] = useState<number|string|null>(null);
    const [awaitValue, setAwaitValue] = useState<number|string|null>(null);

    const open = useCallback(() => { setCrtValue(value); setIsOpen(true); }, [value]);
    const close = useCallback(() => setIsOpen(false), []);

    const onBlur = useCallback(() => {
        if (!inputTransformation) return;
        const newValue = inputTransformation(crtValue);
        setCrtValue(newValue);
        return newValue;
    }, [crtValue]);

    const onConfirm = useCallback(async () => {
        if (action) await action(inputTransformation ? inputTransformation(crtValue) : crtValue);
        setAwaitValue(crtValue);
    }, [action, crtValue, inputTransformation]);

    const onValueChange = useCallback((val: string) => {
        setCrtValue(prev => typeof prev == 'number' ? Number(val) : val);
    }, []);

    useEffect(() => {
        if (value == awaitValue) setAwaitValue(null);
    }, [value, awaitValue]);

    return (
        <>
            { (canEdit && awaitValue == null) && <>
                { children !== undefined && children(open, close, isOpen)}
                { children == undefined &&
                    <MDBBtn
                        tag="a"
                        color="none"
                        onClick={open}
                    >
                        <MDBIcon icon="edit"/>
                    </MDBBtn>
                }
            </>}
            { awaitValue !== null && <MDBSpinner size="sm"/> }
            
            <ActionModal
                open={isOpen}
                canSave={crtValue !== value}
                action={onConfirm}
                onClose={close}
                title={title || t('confirm')}
                confirmText={confirmText || t('confirm')}
            >
                { !customInput &&
                    <MDBInput
                        type={type || "number"}
                        value={crtValue ?? 0}
                        onChange={(ev) => onValueChange(ev.currentTarget.value)}
                        onBlur={onBlur}
                        className="bg-light text-dark"
                        label={inputLabel || t('value')}
                        {...inputProps}
                    />
                }
                { customInput !== undefined && customInput(crtValue, setCrtValue)}
            </ActionModal>
        </>
    )
}